import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback — AuthCallback exchanges session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const finish = useCallback((data) => {
    if (data.token) localStorage.setItem("cml_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return finish(data);
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [finish]);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      return finish(data);
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [finish]);

  const exchangeSession = useCallback(async (session_id) => {
    const { data } = await api.post("/auth/session", { session_id });
    return finish(data);
  }, [finish]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout call failed — clearing the session locally anyway:", err);
    }
    localStorage.removeItem("cml_token");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, register, logout, exchangeSession, refresh: checkAuth }),
    [user, loading, login, register, logout, exchangeSession, checkAuth],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
